import DateFnsUtils from '@date-io/date-fns'
import { yupResolver } from '@hookform/resolvers'
import CardContents from '@ifca-root/react-component/src/components/CardList/CardContents'
import MainHeader from '@ifca-root/react-component/src/components/Header/MainHeader'
import { ContentWrapper } from '@ifca-root/react-component/src/components/Layout/ContentWrapper'
import Loading from '@ifca-root/react-component/src/components/Loading/Loading'
import { Checkbox, TextField } from '@material-ui/core'
import { Autocomplete } from '@material-ui/lab'
import {
  KeyboardDatePicker,
  MuiPickersUtilsProvider,
} from '@material-ui/pickers'
import { AccountFooter } from 'components/Footer/AccountFooter'
import {
  useGetAssignedCompanyQuery,
  useGetCompanyNameQuery,
  useGetCreditorAccountCompanyAssignmentNameLazyQuery,
  useGetCreditorTypeQuery,
} from 'generated/graphql'
import React, { useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useHistory, useLocation, useParams } from 'react-router'
import * as yup from 'yup'
import { CommonYupValidation } from 'helpers/Form/YupValidation'
import CheckBoxIcon from '@material-ui/icons/CheckBox'
import CheckBoxOutlineBlankIcon from '@material-ui/icons/CheckBoxOutlineBlank'

interface APCreditorControlSummaryParamsProps {
  CompanyID: string
  TrxStartDate: Date
  TrxEndDate: Date
  CreditorAccountID: string
  CreditorTypeID: string
}

interface CompanyOption {
  Name: string
  CompanyID: string
}

export const APCreditorControlSummaryByTrxDateParamsForm = (props: any) => {
  let history = useHistory()
  const { CompanyID }: any = useParams()
  let location = useLocation()
  let user = JSON.parse(localStorage.getItem('loggedInUser'))
  const editData = location?.state as any
  const latestCompany = localStorage.getItem('latestCompany')
  const ParamsSchema = yup.object().shape({
    CompanyID: CommonYupValidation.requireField('Company is required'),
  })
  const icon = <CheckBoxOutlineBlankIcon fontSize="small" />
  const checkedIcon = <CheckBoxIcon fontSize="small" />

  const { handleSubmit, register, errors, control, watch, setValue } = useForm<
    APCreditorControlSummaryParamsProps
  >({
    defaultValues: {},
    mode: 'all',
    resolver: yupResolver(ParamsSchema),
  })

  const {
    loading: CreditorTypeLoading,
    error: CreditorTypeErrors,
    data: { getCreditorType } = { getCreditorType: [] },
  } = useGetCreditorTypeQuery({
    fetchPolicy: 'network-only',
  })

  const [
    loadCreditorAccountCompanyAssignment,
    {
      loading: CreditorAccountLoading,
      data: { getCreditorAccountCompanyAssignment } = {
        getCreditorAccountCompanyAssignment: [],
      },
    },
  ] = useGetCreditorAccountCompanyAssignmentNameLazyQuery({
    fetchPolicy: 'network-only',
  })

  let getCreditorAccount = getCreditorAccountCompanyAssignment?.map(x => {
    return x?.CreditorAccount
  })

  const {
    loading: CompanyLoading,
    error: CompanyError,
    data: { getCompany } = { getCompany: [] },
  } = useGetCompanyNameQuery({
    variables: { CompanyID },
    fetchPolicy: 'network-only',
    onCompleted: () => {
      if (latestCompany) {
        loadCreditorAccountCompanyAssignment({
          variables: {
            CompanyID: latestCompany,
          },
        })
      }
    },
  })

  const {
    loading: CompanyAssignedLoading,
    error: CompanyAssignedError,
    data: { getAssignedCompanyByUser } = { getAssignedCompanyByUser: [] },
  } = useGetAssignedCompanyQuery({
    fetchPolicy: 'network-only',
    onCompleted: () => {
      if (latestCompany) {
        loadCreditorAccountCompanyAssignment({
          variables: {
            CompanyID: latestCompany,
          },
        })
      }
    },
  })

  const [selectedCreditAccount, setSelectedCreditAccount]: any = useState(
    new Set()
  )

  const handleCheckboxCreditorAccountChange = (event: any) => {
    let creditorAccount = selectedCreditAccount

    if (event?.target?.checked) {
      creditorAccount.add(event?.target?.value)
    } else {
      creditorAccount.delete(event?.target?.value)
    }
    setSelectedCreditAccount(new Set(creditorAccount))
  }

  const [selectedType, setSelectedType] = useState(new Set())

  const handleCheckboxCreditorTypeChange = (event: any) => {
    let creditorType = selectedType
    if (event?.target?.checked) {
      creditorType.add(event?.target?.value)
    } else {
      creditorType.delete(event?.target?.value)
    }
    setSelectedType(new Set(creditorType))
  }

  const onSubmit = (data, status) => {
    history.push({
      pathname: `/digital-reports/account-payable/ap-creditor-control-summary-by-trx-date/generated`,
      state: {
        CompanyID: data?.CompanyID,
        TrxStartDate: new Date(data.TrxStartDate),
        TrxEndDate: new Date(data.TrxEndDate),
        CreditorAccountID: selectedCreditAccount
          ? Array.from(selectedCreditAccount)
          : undefined,
        CreditorTypeID: selectedType ? Array.from(selectedType) : undefined,
      },
    })
  }

  return (
    <>
      {CompanyLoading && <Loading />}
      {CreditorTypeLoading && <Loading />}
      {CompanyAssignedLoading && <Loading />}
      {CreditorAccountLoading && <Loading />}
      <MainHeader
        mainBtn="back"
        onClick={() => history.push(`/digital-reports/account-payable`)}
        smTitle={'Account Payable'}
        title={user?.companyName}
        routeSegments={[
          { name: 'Main Menu' },
          { name: 'Submenu' },
          {
            name: 'Creditor Control Summary By Transaction Date',
            current: true,
          },
        ]}
        rightRouteSegments={[{ name: 'Parameters', current: true }]}
      />
      <ContentWrapper float>
        <CardContents>
          {!CompanyAssignedLoading && !CompanyLoading && (
            <Controller
              render={({ value, onChange }) => {
                const options: CompanyOption[] =
                  !!user?.superUser === false
                    ? getAssignedCompanyByUser
                    : getCompany
                const defVal = getCompany?.filter(
                  x => x?.CompanyID === latestCompany
                )[0]
                return (
                  <Autocomplete
                    options={options || []}
                    getOptionLabel={(option: CompanyOption) => option.Name}
                    fullWidth
                    onChange={(value, newValue: any) => {
                      setValue('CompanyID', newValue?.CompanyID)

                      loadCreditorAccountCompanyAssignment({
                        variables: {
                          CompanyID: newValue?.CompanyID,
                        },
                      })
                    }}
                    defaultValue={defVal}
                    renderOption={(props, option) => {
                      return <span>{props?.Name}</span>
                    }}
                    renderInput={(params: any) => {
                      return (
                        <div>
                          <TextField
                            {...params}
                            helperText={errors?.CompanyID?.message}
                            error={errors?.CompanyID ? true : false}
                            label="Company"
                            style={{ width: '100%' }}
                            margin="normal"
                            required
                            defaultValue={defVal}
                          />
                        </div>
                      )
                    }}
                  />
                )
              }}
              label="Company"
              name="CompanyID"
              autoComplete="off"
              control={control}
              multiline={true}
              fullWidth
              margin="normal"
              ref={register}
              helperText={errors?.CompanyID?.message}
              error={errors?.CompanyID ? true : false}
              required
              defaultValue={latestCompany}
            />
          )}
          <MuiPickersUtilsProvider utils={DateFnsUtils}>
            <Controller
              required
              as={KeyboardDatePicker}
              name="TrxStartDate"
              label="Transaction Start Date"
              control={control}
              onChange={(date: Date | null) => {}}
              format="dd/MM/yyyy"
              value={watch('TrxStartDate')}
              margin="normal"
              allowKeyboardControl
              ref={register}
              KeyboardButtonProps={{
                'aria-label': 'change date',
              }}
              defaultValue={editData?.TrxStartDate ?? new Date()}
              helperText={errors?.TrxStartDate?.message}
              error={errors?.TrxStartDate ? true : false}
              showTodayButton
              fullWidth
              className="left"
            />
          </MuiPickersUtilsProvider>

          <MuiPickersUtilsProvider utils={DateFnsUtils}>
            <Controller
              required
              as={KeyboardDatePicker}
              name="TrxEndDate"
              label="Transaction End Date"
              control={control}
              onChange={(date: Date | null) => {}}
              format="dd/MM/yyyy"
              value={watch('TrxEndDate')}
              margin="normal"
              allowKeyboardControl
              ref={register}
              KeyboardButtonProps={{
                'aria-label': 'change date',
              }}
              defaultValue={editData?.TrxEndDate ?? new Date()}
              helperText={errors?.TrxEndDate?.message}
              error={errors?.TrxEndDate ? true : false}
              showTodayButton
              fullWidth
              className="right"
            />
          </MuiPickersUtilsProvider>

          <Controller
            render={({ value, onChange }) => {
              return (
                <Autocomplete
                  multiple
                  options={getCreditorAccount || []}
                  getOptionLabel={option => option?.CompanyName}
                  fullWidth
                  onChange={(value, newValue: any) => {
                    const selecteted = new Set()

                    newValue?.map(x => {
                      selecteted.add(x?.CreditorAccountID)
                    })

                    setSelectedCreditAccount(new Set(selecteted))
                  }}
                  renderOption={(option, { selected }) => (
                    <React.Fragment>
                      <Checkbox
                        icon={icon}
                        checkedIcon={checkedIcon}
                        value={option?.CreditorAccountID}
                        style={{ marginRight: 8 }}
                        checked={selected}
                        color="primary"
                        onChange={event => {
                          handleCheckboxCreditorAccountChange(event)
                        }}
                      />
                      {option?.CompanyName}
                    </React.Fragment>
                  )}
                  renderInput={(params: any) => {
                    return (
                      <div>
                        <TextField
                          {...params}
                          variant="outlined"
                          style={{ width: '100%' }}
                          label="Creditor Account"
                          margin="normal"
                        />
                      </div>
                    )
                  }}
                />
              )
            }}
            name="CreditorAccountID"
            label="Creditor Account"
            margin="normal"
            autoComplete="off"
            fullWidth
            multiline={true}
            control={control}
            select
            ref={register}
          />

          <Controller
            render={({ value, onChange }) => {
              return (
                <Autocomplete
                  multiple
                  options={getCreditorType || []}
                  getOptionLabel={option =>
                    `${option?.CreditorType}=>${option?.Description}`
                  }
                  fullWidth
                  onChange={(value, newValue: any) => {
                    const selecteted = new Set()

                    newValue?.map(x => {
                      selecteted.add(x?.CreditorTypeID)
                    })

                    setSelectedType(new Set(selecteted))
                  }}
                  renderOption={(option, { selected }) => (
                    <React.Fragment>
                      <Checkbox
                        icon={icon}
                        checkedIcon={checkedIcon}
                        value={option?.CreditorTypeID}
                        style={{ marginRight: 8 }}
                        checked={selected}
                        color="primary"
                        onChange={event => {
                          handleCheckboxCreditorTypeChange(event)
                        }}
                      />
                      {`${option?.CreditorType}=>${option?.Description}`}
                    </React.Fragment>
                  )}
                  renderInput={(params: any) => {
                    return (
                      <div>
                        <TextField
                          {...params}
                          variant="outlined"
                          style={{ width: '100%' }}
                          label="Creditor Type"
                          margin="normal"
                        />
                      </div>
                    )
                  }}
                />
              )
            }}
            name="CreditorTypeID"
            label="Creditor Type"
            margin="normal"
            autoComplete="off"
            fullWidth
            multiline={true}
            control={control}
            select
            ref={register}
          />
        </CardContents>

        <AccountFooter
          options={[
            {
              name: 'Submit',
              onClick: () => {
                handleSubmit(onSubmit)()
              },
              color: 'primary',
            },
          ]}
        />
      </ContentWrapper>
      {/* FOOTER */}
    </>
  )
}
